import { cn } from "@lib/utils/cn";
import type { Table as TanStackTable } from "@tanstack/react-table";

interface DataTablePageNumbersProps<TData> {
  table: TanStackTable<TData>;
}

export function DataTablePageNumbers<TData>({ table }: DataTablePageNumbersProps<TData>) {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  // Don't render if 1 or fewer pages
  if (pageCount <= 1) return null;

  const getPageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];

    // Always show page 1
    pages.push(0);

    if (pageCount <= 7) {
      // Show all pages without ellipsis
      for (let i = 1; i < pageCount; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Smart ellipsis for 7+ pages
    if (currentPage <= 3) {
      // Near start: 1 2 3 4 5 ... last
      for (let i = 1; i <= Math.min(5, pageCount - 2); i++) {
        pages.push(i);
      }
      if (pageCount - 1 > 5) {
        pages.push("ellipsis");
      }
      pages.push(pageCount - 1);
    } else if (currentPage >= pageCount - 4) {
      // Near end: 1 ... n-4 n-3 n-2 n-1 last
      pages.push("ellipsis");
      for (let i = pageCount - 5; i < pageCount; i++) {
        pages.push(i);
      }
    } else {
      // Middle: 1 ... current-1 current current+1 ... last
      pages.push("ellipsis");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("ellipsis");
      pages.push(pageCount - 1);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <>
      {pageNumbers.map((page, idx) =>
        page === "ellipsis" ? (
          <span
            key={`ellipsis-${idx}`}
            className="join-item btn btn-sm btn-ghost pointer-events-none"
          >
            ...
          </span>
        ) : (
          <button
            key={page}
            type="button"
            className={cn("join-item btn btn-sm", page === currentPage && "btn-active")}
            onClick={() => table.setPageIndex(page)}
            aria-current={page === currentPage ? "page" : undefined}
            aria-label={`Page ${page + 1}`}
          >
            {page + 1}
          </button>
        ),
      )}
    </>
  );
}
